import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Brain, Send, Loader2, Sparkles, ArrowLeft, Pill,
  Shield, LayoutDashboard, Plus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POPULAR_QUESTIONS = [
  "What are the benefits of Vitamin D?",
  "Is it safe to take Melatonin daily?",
  "What does Ashwagandha do?",
  "Are there side effects of Omega-3?",
  "What is Magnesium good for?",
  "Benefits of Turmeric/Curcumin?"
];

// Format AI text with markdown-like styling
const FormatText = ({ text }) => {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, i) => {
    if (line.startsWith('###')) {
      return <h4 key={i} className="font-semibold text-[#1A1A1A] mt-3 mb-1 text-sm">{line.replace(/^###\s*/, '')}</h4>;
    }
    if (line.startsWith('##')) {
      return <h3 key={i} className="font-bold text-[#1A1A1A] mt-3 mb-1">{line.replace(/^##\s*/, '')}</h3>;
    }
    if (line.startsWith('- ') || line.startsWith('• ')) {
      const content = line.replace(/^[-•]\s*/, '');
      return (
        <div key={i} className="flex items-start gap-2 ml-1 my-0.5">
          <span className="text-primary mt-1 text-xs">•</span>
          <span className="text-sm">{formatBold(content)}</span>
        </div>
      );
    }
    if (line.match(/^\d+\.\s/)) {
      return (
        <div key={i} className="flex items-start gap-2 ml-1 my-0.5">
          <span className="text-primary font-medium text-sm min-w-[18px]">{line.match(/^(\d+\.)/)[1]}</span>
          <span className="text-sm">{formatBold(line.replace(/^\d+\.\s*/, ''))}</span>
        </div>
      );
    }
    if (line.toLowerCase().includes('key takeaway')) {
      return (
        <div key={i} className="mt-3 p-3 bg-primary/10 rounded-lg border border-primary/20">
          <p className="font-semibold text-primary text-sm">{formatBold(line)}</p>
        </div>
      );
    }
    if (!line.trim()) return <div key={i} className="h-1.5" />;
    return <p key={i} className="my-0.5 text-sm">{formatBold(line)}</p>;
  });
};

function formatBold(text) {
  if (!text.includes('**')) return text;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part);
}


export default function AskAIPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [medications, setMedications] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?redirect=/ask');
    }
  }, [user, authLoading, navigate]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const sendMessage = async (text) => {
    if (!text?.trim() || loading) return;
    const userMsg = text.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);
    scrollToBottom();

    try {
      const token = localStorage.getItem('vitala_token');
      const response = await axios.post(
        `${API}/ask/chat`,
        { message: userMsg, session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );

      if (!sessionId) setSessionId(response.data.session_id);
      if (response.data.medications_considered?.length > 0) {
        setMedications(response.data.medications_considered);
      }

      setMessages(prev => [...prev, { role: 'assistant', text: response.data.answer }]);
    } catch (error) {
      toast.error('Failed to get a response. Please try again.');
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I encountered an error. Please try again.', error: true }]);
    } finally {
      setLoading(false);
      scrollToBottom();
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    sendMessage(input);
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setMedications([]);
    setInput('');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return null;

  const hasMessages = messages.length > 0;

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-muted-foreground hover:text-primary">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <Brain className="w-4 h-4 text-primary" />
              </div>
              <span className="font-semibold text-[#1A1A1A] text-sm">Vitala AI</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasMessages && (
              <Button variant="outline" size="sm" onClick={startNewChat} className="text-xs" data-testid="new-chat-btn">
                <Plus className="w-4 h-4 mr-1" />
                New Chat
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} className="text-xs">
              <LayoutDashboard className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Chat Area */}
      <main className="flex-1 max-w-4xl mx-auto w-full flex flex-col" style={{ minHeight: 'calc(100vh - 130px)' }}>
        <div className="flex-1 px-4 sm:px-6 py-4 overflow-y-auto">
          {/* Welcome / Empty State */}
          {!hasMessages && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center mb-4">
                <Brain className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#1A1A1A] mb-2 text-center">
                Ask About Supplements
              </h1>
              <p className="text-muted-foreground text-sm text-center max-w-md mb-6">
                Ask anything about supplements — benefits, side effects, dosages, interactions. I'll remember our conversation for follow-ups.
              </p>

              {/* Popular Questions */}
              <div className="w-full max-w-lg">
                <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1 justify-center">
                  <Sparkles className="w-3 h-3" /> Try asking:
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {POPULAR_QUESTIONS.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(q)}
                      className="text-xs px-3 py-2 bg-white border border-border hover:border-primary/30 hover:bg-primary/5 rounded-full text-[#1A1A1A] transition-colors"
                      data-testid={`popular-q-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Medications Notice */}
          {medications.length > 0 && messages.length <= 2 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-2">
                <Pill className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  <span className="font-medium">Your medications considered:</span> {medications.join(', ')}
                </p>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="space-y-4">
            <AnimatePresence>
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'user' ? (
                    <div className="bg-primary text-white rounded-2xl rounded-br-sm px-4 py-3 max-w-[85%]" data-testid={`user-msg-${i}`}>
                      <p className="text-sm">{msg.text}</p>
                    </div>
                  ) : (
                    <div className={`bg-white border ${msg.error ? 'border-red-200' : 'border-border'} rounded-2xl rounded-bl-sm px-4 py-3 max-w-[90%]`} data-testid={`ai-msg-${i}`}>
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border/50">
                        <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                          <Brain className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground">Vitala AI</span>
                      </div>
                      <div className="text-[#1A1A1A] leading-relaxed">
                        <FormatText text={msg.text} />
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Loading indicator */}
            {loading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Thinking...</span>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input — always fixed at bottom */}
        <div className="border-t border-border bg-white px-4 sm:px-6 py-3 sticky bottom-0">
          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto flex items-end gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder={hasMessages ? "Type your follow-up..." : "Ask about any supplement..."}
                rows={1}
                className="w-full resize-none rounded-xl border border-border bg-[#FAFAF9] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                disabled={loading}
                data-testid="ask-input"
                style={{ minHeight: '44px', maxHeight: '120px' }}
              />
            </div>
            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="btn-primary rounded-xl h-11 w-11 p-0 flex-shrink-0"
              data-testid="ask-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </form>
          <div className="flex items-center gap-1 mt-1.5 justify-center">
            <Shield className="w-3 h-3 text-muted-foreground" />
            <p className="text-[10px] text-muted-foreground">For educational purposes only. Consult a healthcare provider before starting supplements.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
